import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  InputGroup,
  Badge,
  Dropdown,
  Spinner
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSearch,
  faEdit,
  faEye,
  faSort,
  faDownload,
  faEllipsisV,
  faSync
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams, useHistory } from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'
import * as PurchaseOrderController from '../Controller/PurchaseOrderController'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import '../Assets/css/purchase-order.scss'
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction'

const PurchaseOrderList = (props) => {
  const { t, i18n } = props;
  const history = useHistory();
  const lng = i18n.language;

  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState('id')
  const [sortDirection, setSortDirection] = useState('desc')
  const [totalItems, setTotalItems] = useState(0)
  const [selectedRange, setSelectedRange] = useState('all')
  const [exporting, setExporting] = useState(false)
  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    sort: [
      {
        field: 'id',
        sort: 'desc'
      }
    ],
    search: '',
    from_created_date: '',
    to_created_date: ''
  })
  const suppressNextFetchRef = useRef(false)

  const setQuickRange = (type) => {
    const today = new Date()
    let from = ''
    let to = ''

    const fmt = (d) => d.toISOString().split('T')[0]

    switch (type) {
      case 'today':
        from = fmt(today)
        to = fmt(today)
        break
      case 'yesterday': {
        const y = new Date(today)
        y.setDate(y.getDate() - 1)
        from = fmt(y)
        to = fmt(y)
        break
      }
      case 'last7': {
        const f = new Date(today)
        f.setDate(f.getDate() - 6) // include today => last 7 days
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'lastMonth': {
        const f = new Date(today)
        f.setDate(f.getDate() - 29) // last 30 days including today
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'all':
        from = ''
        to = ''
        break
      default:
        break
    }

    setFilters((prev) => ({
      ...prev,
      from_created_date: from,
      to_created_date: to,
      offset: 0
    }))
    setSelectedRange(type)
  }

  const handleQuickRangeClick = (e) => {
    const range = e.currentTarget.getAttribute('data-range')
    if (range === 'custom') {
      setSelectedRange('custom')
      return
    }
    if (range === 'today') return setQuickRange('today')
    if (range === 'yesterday') return setQuickRange('yesterday')
    if (range === 'last7') return setQuickRange('last7')
    if (range === 'lastMonth') return setQuickRange('lastMonth')
    if (range === 'all') return setQuickRange('all')
  }

  const handleDateChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: 0
    }))
  }

  // keep for parity; fetch triggers automatically when both dates set

  // Summary moved to Pagination at bottom-right

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1) // Reset to first page when changing limit
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      offset: 0
    }))
  }

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        props.loaderStateTrue()
        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
        }

        const response = await PurchaseOrderController.getPurchaseOrdersApi(
          effectiveFilters
        )
        if (response.success) {
          const rows = response.data || []
          setPurchaseOrders(rows)
          // Use backend total for correct pagination
          setTotalItems(response.total || rows.length)
        } else {
          setPurchaseOrders([])
        }
      } catch (error) {
        console.error('Error fetching purchase orders:', error)
        setPurchaseOrders([])
      } finally {
        props.loaderStateFalse()
      }
    },
    [props.userEntryType]
  )
  const fetchPurchaseOrders = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    fetchPurchaseOrders()
  }, [fetchPurchaseOrders])

  // Auto-fetch when Custom Dates are both selected (match Quotation behavior)
  useEffect(() => {
    if (
      selectedRange === 'custom' &&
      filters.from_created_date &&
      filters.to_created_date
    ) {
      performFetch({ ...filters })
    }
  }, [selectedRange, filters, performFetch])

  const getCreatedDisplay = (row) => {
    const value =
      row.createdAt || row.created_at || row.createdAt_label || row.created_on
    return value ? Utility.getDateFormat(value) : 'N/A'
  }

  const getUpdatedDisplay = (row) => {
    const value =
      row.updatedAt || row.updated_at || row.updatedAt_label || row.updated_on
    return value ? Utility.getDateFormat(value) : 'N/A'
  }

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) {
      performFetch(next)
    }
  }

  // Debounced onChange is used; no Enter handler required

  const handleExport = useCallback(async () => {
    try {
      setExporting(true)

      const exportData = {
        from_created_date: filters.from_created_date,
        to_created_date: filters.to_created_date
      }

      const response = await PurchaseOrderController.exportPurchaseOrdersApi(
        exportData
      )

      if (response && response.success && response.data) {
        if (response.isFileUrl) {
          await Utility.downloadFile(
            response.data,
            response.filename || 'purchase_orders_export.xlsx',
            () =>
              Utility.toastNotifications(
                'Purchase orders exported successfully',
                'Success',
                'success'
              ),
            (error) =>
              console.error('Failed to download file: ' + error.message)
          )
        } else {
          const blob = new Blob([response.data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })

          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = response.filename || 'purchase_orders_export.xlsx'
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
          Utility.toastNotifications(
            'Purchase orders exported successfully',
            'Success',
            'success'
          )
        }
      } else {
        // Failed to export purchase orders
      }
    } catch (error) {
      console.error('Error exporting purchase orders:', error)
    } finally {
      setExporting(false)
    }
  }, [filters])

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit
    }))
  }

  const handleSort = (field) => {
    let newSortDirection = 'asc'

    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      setSortField(field)
    }

    setSortDirection(newSortDirection)

    setFilters((prev) => ({
      ...prev,
      sort: [
        {
          field: field,
          sort: newSortDirection
        }
      ]
    }))
  }

  const handleRefresh = () => {
    fetchPurchaseOrders()
  }

  const getStatusBadge = (statusOrRow) => {
    // Prefer exact backend-provided status_data label and color
    if (
      statusOrRow &&
      typeof statusOrRow === 'object' &&
      statusOrRow.status_data
    ) {
      const sd = statusOrRow.status_data
      const label = sd.label || sd.value_constant || sd.value || 'Unknown'
      const colorRaw = String(sd.color || '')

      let variant = 'secondary' // default

      if (colorRaw) {
        // Extract a meaningful color token from strings like 'label red-label'
        const colorToken = (colorRaw.match(
          /red|green|yellow|blue|orange|purple|pink|grey|gray|primary|secondary|success|warning|danger|info/i
        ) || [''])[0].toLowerCase()
        const mapToVariant = {
          red: 'danger',
          green: 'success',
          yellow: 'warning',
          blue: 'primary',
          orange: 'warning',
          purple: 'info',
          pink: 'danger',
          grey: 'secondary',
          gray: 'secondary',
          primary: 'primary',
          secondary: 'secondary',
          success: 'success',
          warning: 'warning',
          danger: 'danger',
          info: 'info'
        }
        variant = mapToVariant[colorToken] || 'secondary'
      } else {
        // Fallback: map based on value_constant or status value
        const statusConstant = sd.value_constant || sd.value
        const statusValue = parseInt(statusOrRow.status)

        const statusColorMap = {
          CANCELLED: 'danger', // red
          CREATED: 'primary', // blue
          APPROVED: 'success', // green
          RELEASED_TO_SUPPLIER: 'warning', // orange
          CLOSED: 'info', // light blue
          // Fallback numeric mapping
          0: 'danger', // Cancelled = red
          1: 'primary', // Created = blue
          2: 'success', // Approved = green
          3: 'warning', // Released = orange
          4: 'info' // Closed = light blue
        }

        variant =
          statusColorMap[statusConstant] ||
          statusColorMap[statusValue] ||
          'secondary'
      }

      return <Badge bg={variant}>{label}</Badge>
    }

    // Legacy fallback when only numeric status is present
    const statusValue = statusOrRow
    const statusMap = {
      0: { label: 'Cancelled', bg: 'secondary' },
      1: { label: 'Created', bg: 'primary' },
      2: { label: 'Approved', bg: 'success' },
      3: { label: 'Released', bg: 'warning' },
      4: { label: 'Closed', bg: 'info' }
    }
    const statusInfo = statusMap[statusValue] || {
      label: 'Unknown',
      bg: 'primary'
    }
    return <Badge bg={statusInfo.bg}>{statusInfo.label}</Badge>
  }


  const hasPurchaseOrderPermission = () => {
    const purchasePermission = Utility.getRoleObjectBySubEventKey('sales_and_orders', 'purchase_orders');
    if (purchasePermission.length > 0) {
      const rules = purchasePermission[0].rules;
      return rules === "CRUD" || rules === "CUD";
    }
    return false;
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const purchaseOrderPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'purchase_orders',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'generate_invoice', 'change_status'],
    props.userCredentials?.user_details?.user_type
  );

  // If rules are CRUD or CUD, automatically enable view permission
  if (hasPurchaseOrderPermission()) {
    purchaseOrderPermissions.view = true;
  }

  // Check if any actions are available for display in the table
  const hamburgerTableActions = purchaseOrderPermissions.view || purchaseOrderPermissions.edit || purchaseOrderPermissions.delete;

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>{t('purchaseOrders.title')}</h4>
        <div className='orderBox'>
          {purchaseOrderPermissions.export && (
            <Button
              variant='outline-secondary'
              className='me-2'
              disabled={exporting}
              onClick={() => handleExport()}
            >
              <FontAwesomeIcon icon={faDownload} className='me-2' /> {exporting ? 'Exporting...' : t('common.buttons.export')}
            </Button>
          )}
          {hasPurchaseOrderPermission() && purchaseOrderPermissions.create && (
            <Button
              variant='primary'
              onClick={() => history.push(`/${lng}/sales/purchase-orders/add`)}
            >
              <FontAwesomeIcon icon={faPlus} className='me-2' /> {t('purchaseOrders.buttons.addPurchaseOrder')}
            </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          {/* Row 1: Only date inputs with labels above + Search button */}
          {/* <Row className='mb-3'>
            <Col md={6} lg={4} className='mb-2'>
              <label className='form-label'>{t('purchaseOrders.labels.fromDate')}</label>
              <input
                type='date'
                className='form-control'
                placeholder={t('purchaseOrders.messages.selectFromCreatedDate')}
                value={filters.from_created_date}
                onChange={(e) =>
                  handleDateChange('from_created_date', e.target.value)
                }
              />
            </Col>
            <Col md={6} lg={4} className='mb-2'>
              <label className='form-label'>{t('purchaseOrders.labels.toDate')}</label>
              <input
                type='date'
                className='form-control'
                placeholder={t('purchaseOrders.messages.selectToCreatedDate')}
                value={filters.to_created_date}
                min={filters.from_created_date || undefined}
                onChange={(e) =>
                  handleDateChange('to_created_date', e.target.value)
                }
              />
            </Col>
            <Col md={6} lg={4} className='d-flex align-items-end mb-2'>
              <Button variant='primary' onClick={fetchPurchaseOrders}>
                {t('purchaseOrders.search.title')}
              </Button>
            </Col>
          </Row> */}

          {/* Row 2: Showing info, quick buttons, show entries, and search input */}
          <Row className='mb-3 gx-2 searchBarPanel'>
            <Col className='d-flex align-items-center flex-wrap gap-2 innSearchBarPanel'>
              {selectedRange === 'custom' && (
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    value={filters.from_created_date}
                    onChange={(e) => {
                      handleDateChange('from_created_date', e.target.value)
                      if (e.target.value && e.target.value > filters.to_created_date) {
                        handleDateChange('to_created_date', '')
                      }
                    }}
                    style={{ width: 160 }}
                  />
                  <span>to</span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    min={filters.from_created_date || undefined}
                    value={filters.to_created_date}
                    onChange={(e) =>
                      handleDateChange('to_created_date', e.target.value)
                    }
                    style={{ width: 160 }}
                  />
                </div>
              )}
              <div className='btn-group ms-auto'>
                <Button
                  size='sm'
                  variant={selectedRange === 'today' ? 'primary' : 'light'}
                  data-range='today'
                  onClick={handleQuickRangeClick}
                >
                  {t('purchaseOrders.labels.today')}
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'yesterday' ? 'primary' : 'light'}
                  data-range='yesterday'
                  onClick={handleQuickRangeClick}
                >
                  {t('purchaseOrders.labels.yesterday')}
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'last7' ? 'primary' : 'light'}
                  data-range='last7'
                  onClick={handleQuickRangeClick}
                >
                  {t('purchaseOrders.labels.last7days')}
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'lastMonth' ? 'primary' : 'light'}
                  data-range='lastMonth'
                  onClick={handleQuickRangeClick}
                >
                  {t('purchaseOrders.labels.lastMonth')}
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'custom' ? 'primary' : 'light'}
                  data-range='custom'
                  onClick={handleQuickRangeClick}
                >
                  Custom Dates
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'all' ? 'primary' : 'light'}
                  data-range='all'
                  onClick={handleQuickRangeClick}
                >
                  {t('purchaseOrders.labels.all')}
                </Button>
              </div>
              {/* <div className='d-flex align-items-center gap-2 ms-2'>
                <span>{t('purchaseOrders.labels.show')}</span>
                <select
                  className='form-select form-select-sm'
                  style={{ width: 80 }}
                  value={itemsPerPage}
                  onChange={(e) => handleLimitChange(e.target.value)}
                >
                  {[10, 25, 50, 100, 500, 1000].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span>{t('purchaseOrders.labels.entries')}</span>
              </div> */}
              <div className='searchFieldBoxes'>
                <div className='ms-2 searchFieldBox searchFieldBoxCustom'>
                  <InputGroup size='sm'>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <DebounceInput
                      minLength={1}
                      debounceTimeout={500}
                      className='form-control'
                      placeholder={t('purchaseOrders.search.search')}
                      value={searchTerm}
                      onChange={(e) => handleSearch(e)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault()
                      }}
                    />
                  </InputGroup>
                </div>
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={props.loaderState}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={props.loaderState}
                    />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('po_number')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.poNumber')}{' '}
                    {sortField === 'po_number' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('po_reference')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.poReference')}{' '}
                    {sortField === 'po_reference' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('supplier_name')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.supplier')}{' '}
                    {sortField === 'supplier_name' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th>
                    Store
                  </th>
                  <th>
                    Items
                  </th>
                  <th
                    onClick={() => handleSort('order_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.orderDate')}{' '}
                    {sortField === 'order_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('order_due_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Delivery Due Date{' '}
                    {sortField === 'order_due_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('total_order_amount')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.amount')}{' '}
                    {sortField === 'total_order_amount' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.status')}{' '}
                    {sortField === 'status' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('createdAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.createdOn')}{' '}
                    {sortField === 'created_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('updatedAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.updatedOn')}{' '}
                    {sortField === 'updated_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                  <th
                    onClick={() => handleSort('created_by')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('purchaseOrders.labels.createdBy')}{' '}
                    {sortField === 'created_by' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {hamburgerTableActions && (
                    <th>{t('purchaseOrders.labels.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {props.loaderState ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '14' : '13'} className='text-center py-4'>
                      {/* <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>{t('purchaseOrders.messages.loadingPurchaseOrders')}</span> */}
                    </td>
                  </tr>
                ) : purchaseOrders.length > 0 ? (
                  purchaseOrders.map((purchaseOrder) => {
                    return (
                      <tr
                        key={purchaseOrder.id}
                        onDoubleClick={() => history.push(`/${lng}/sales/purchase-orders/view/${purchaseOrder.slack}`)}
                        className='cursor-pointer'
                      >
                        <td>{purchaseOrder.po_number}</td>
                        <td>{purchaseOrder.po_reference || 'N/A'}</td>
                        <td>{purchaseOrder.supplier_name}</td>
                        <td>{purchaseOrder.store?.name || 'N/A'}</td>
                        <td>{purchaseOrder.products?.length || 0}</td>
                        <td>{purchaseOrder.order_date}</td>
                        <td>{purchaseOrder.order_due_date}</td>
                        <td>
                          {parseFloat(
                            purchaseOrder.total_order_amount || 0
                          ).toFixed(2)}
                        </td>
                        <td>
                          {(() => {
                            // console.log('=== FULL PURCHASE ORDER OBJECT ===')
                            // console.log('ID:', purchaseOrder.id)
                            // console.log('status:', purchaseOrder.status)
                            // console.log(
                            //   'status_data:',
                            //   purchaseOrder.status_data
                            // )
                            // console.log(
                            //   'status_data.color:',
                            //   purchaseOrder.status_data?.color
                            // )
                            // console.log(
                            //   'Full object keys:',
                            //   Object.keys(purchaseOrder)
                            // )
                            // console.log('===================================')
                            return getStatusBadge(purchaseOrder)
                          })()}
                        </td>
                        <td>{getCreatedDisplay(purchaseOrder)}</td>
                        {/* <td>{getUpdatedDisplay(purchaseOrder)}</td> */}
                        <td>
                          {(() => {
                            const u =
                              purchaseOrder.created_by ||
                              purchaseOrder.created_user
                            if (!u) return 'N/A'
                            if (typeof u === 'string') return u
                            return (
                              u.username ||
                              u.name ||
                              u.fullname ||
                              u.email ||
                              'N/A'
                            )
                          })()}
                        </td>
                        <td>
                          {hamburgerTableActions && (
                            <Dropdown align='end'>
                              <Dropdown.Toggle
                                variant='light'
                                size='sm'
                                id={`dropdown-${purchaseOrder.id}`}
                                className='btn-icon no-caret'
                              >
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {purchaseOrderPermissions.view && (
                                  <Dropdown.Item
                                    as={Link}
                                    to={`/${lng}/sales/purchase-orders/view/${purchaseOrder.slack}`}
                                  >
                                    <FontAwesomeIcon
                                      icon={faEye}
                                      className='me-2'
                                    />{' '}
                                    {t('purchaseOrders.buttons.view')}
                                  </Dropdown.Item>
                                )}

                                {/* Edit - only for Created and Approved status */}
                                {purchaseOrderPermissions.edit &&
                                  (purchaseOrder.status === 1 ||
                                    purchaseOrder.status === 2) && (
                                    <Dropdown.Item
                                      as={Link}
                                      to={`/${lng}/sales/purchase-orders/edit/${purchaseOrder.slack}`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faEdit}
                                        className='me-2'
                                      />{' '}
                                      Edit
                                    </Dropdown.Item>
                                  )
                                }
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '12' : '11'} className='text-center py-4'>
                      <div className='no-record-found'>
                        {searchTerm
                          ? t('purchaseOrders.messages.noPurchaseOrdersmsg')
                          : t('purchaseOrders.messages.noPurchaseOrdersFound')}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType,
    loaderState: globalState.mainReducerData.loaderState
  }
}

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse, handleActiveLink })(withNamespaces()(PurchaseOrderList))
